// CardAiChat.tsx
import React, { useEffect, useRef, useState } from 'react';
import CardLayout from "../../common/CardLayout";
import { 
  Segment, 
  Header, 
  Icon, 
  Divider, 
  Message, 
  Dimmer,
  Loader,
  Button } from 'semantic-ui-react';
import ChatHistory from './ChatHistory';
import ChatInput from './ChatInput';
import { useIntl } from "react-intl";
import AiChatManager, { ChatMessage, ChatSession, PatientContext } from './services/AiChatManager';
import "./CardAiChat.scss";


// Props
type CardAiChatProps = {
  runSequence?: any;
  aiChatManager?: AiChatManager;
  isLoading?: boolean;
  selectedPatient?: any;
};

const CardAiChat = (props: CardAiChatProps) => {
  const messagesEndRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [currentSession, setCurrentSession] = useState<ChatSession | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [patientContext, setPatientContext] = useState<PatientContext | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  // Setup event handlers for AiChatManager
  useEffect(() => {
    if (props.aiChatManager) {
      console.log("AiChatManager initialized");
      props.aiChatManager.onMessagesUpdated = (newMessages: ChatMessage[]) => {
        setMessages(newMessages);
        scrollToBottom();
      };
      
      props.aiChatManager.onSessionChanged = (sessionId: string | null) => {
        console.log('Session changed:', sessionId);
      };
      
      props.aiChatManager.onPatientContextChanged = (context: PatientContext | null) => {
        setPatientContext(context);
      };
      
      props.aiChatManager.onError = (errorMessage: string) => {
        setError(errorMessage);
        setLoading(false);
      };

      console.log(props.aiChatManager)
    }
  }, [props.aiChatManager]);

  // Handle patient selection
  useEffect(() => {
    console.log("selectedPatient", props.selectedPatient);
    setPatientContext(null);
    setMessages([]);
    
    if (props.selectedPatient && props.aiChatManager) {
      setLoading(true);
      setError(null);
      
      // Search for existing sessions for this patient
      props.aiChatManager.searchSessionsByHn(props.selectedPatient.hn, 30)
        .then(sessions => {
          console.log("Existing sessions found:", sessions);
          
          if (sessions.length > 0) {
            const session = sessions[0];
            setCurrentSession(session);
            console.log("Using existing session:", session);
            
            // Activate the existing session
            // Try to activate the session if it exists and checking the expiration
            props.aiChatManager?.activateSession(session.session_id);
          } else {
            // No existing session, will need to create one
            setCurrentSession(null);
          }
        })
        .catch(error => {
          console.error("Error searching for sessions:", error);
          // setError("Failed to search for existing sessions");
          // The most case is that the session is expire, so we can ignore this error
          // The user can start a new session
          setCurrentSession(null);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      // No patient selected, reset state
      setCurrentSession(null);
      setMessages([]);
      setPatientContext(null);
    }
  }, [props.selectedPatient, props.aiChatManager]);

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const startChatClick = async () => {
    if (!props.aiChatManager) return;
    
    console.log("Start chat clicked");
    setLoading(true);
    setError(null);
    
    try {
      // Create new session
      const session = await props.aiChatManager.createChatSession();
      
      // If patient is selected, set patient in the session
      if (props.selectedPatient) {
        console.log("Setting patient data...");
        const patientData = await props.aiChatManager.setPatient(
          session.session_id, 
          props.selectedPatient.hn
        );
        console.log("Patient data set:", patientData);
        // defer set session until patient data is set
        // purpose is to ensure that the session has patient context
        console.log("Session created:", session);
        setCurrentSession(session);
      }
    } catch (error) {
      console.error("Error creating chat session:", error);
      setError("Failed to create chat session");
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async (message: string) => {
    if (!props.aiChatManager || !currentSession) {
      setError("No active session");
      return;
    }
    
    console.log("Sending message:", message);
    // setLoading(true);
    setError(null);
    
    // try {
    //   await props.aiChatManager.sendMessage(currentSession.session_id, message);
    // } catch (error) {
    //   console.error("Error sending message:", error);
    //   // Error handling is done in AiChatManager
    // } finally {
    //   setLoading(false);
    // }

    try {
      // Use streaming version
      await props.aiChatManager.sendMessageStream(currentSession.session_id, message);
    } catch (error) {
      console.error("Error sending message:", error);
      // Error handling is done in AiChatManager
    }

  };

  const handleSummarize = async () => {
    if (!props.aiChatManager || !currentSession) {
      setError("No active session");
      return;
    }
    
    setSummaryLoading(true);
    setError(null);
    
    try {
      // Use streaming version
      await props.aiChatManager.summarizePatientStream(currentSession.session_id);
    } catch (error) {
      console.error("Error summarizing patient:", error);
      setError("Failed to generate summary");
    } finally {
      setSummaryLoading(false);
    }
  };

  const handleClearHistory = async () => {
    if (!props.aiChatManager || !currentSession) {
      setError("No active session");
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      await props.aiChatManager.deactivateSession(currentSession.session_id);
    } catch (error) {
      console.error("Error clearing history:", error);
    } finally {
      setLoading(false);
      setCurrentSession(null);
    }
  };

  const hasPatientContext = () => {
    return props.selectedPatient || 
           patientContext?.patient_info || 
           currentSession?.patient_name;
  };

  const getPatientDisplayInfo = () => { 
    if (patientContext?.patient_info) {
      return {
        name: patientContext.patient_info.full_name,
        hn: patientContext.patient_info.hn
      };
    }
    
    if (currentSession?.patient_name) {
      return {
        name: currentSession.patient_name,
        hn: currentSession.patient_hn
      };
    }
    
    return null;
  };

  const patientInfo = getPatientDisplayInfo();

  return (
    <CardLayout
      titleText={patientInfo ? patientInfo?.name : "AI Chatbot"}
      style={{ paddingBottom: "0px"}}
      closeable={false}
      toggleable={false}
      hideHeaderIcon={true}
    >
      {patientInfo && (
      <div style={{ top: "8px", right: "8px", position: "absolute" }}>
        <Button
          color="green"
          size="tiny"
          onClick={handleSummarize}
          loading={summaryLoading}
          disabled={summaryLoading || !hasPatientContext()}
        >
          <Icon name="file alternate" />
          Medical History Summary
        </Button>
        <Button
          size="tiny"
          onClick={handleClearHistory}
          loading={loading}
          disabled={loading}
          style={{ marginLeft: '0.5em' }}
        >
          <Icon name="trash" />
          Clear History
        </Button>
      </div>
      )}

      <Segment 
        className="chat-container" 
        style={{ display: 'flex', flexDirection: 'column', height: '70vh', background: '#5DBCD233' }}>
        
        {error && (
          <Message negative onDismiss={() => setError(null)}>
            <Message.Header>Error</Message.Header>
            <p>{error}</p>
          </Message>
        )}
        
        {!currentSession && !loading && (
          <Dimmer.Dimmable>
            <Dimmer active={false} inverted>
              <Loader />
            </Dimmer>
            <div style={{ textAlign: 'center', padding: '2em' }}>
              <Button
                primary
                size="large"
                onClick={startChatClick}
                loading={loading}
                disabled={loading}
              >
                Start Conversation
              </Button>
            </div>
          </Dimmer.Dimmable>
        )}
        
        {loading && !currentSession && (
          <Dimmer active inverted>
            <Loader>Preparing Conversation...</Loader>
          </Dimmer>
        )}
        
        {currentSession && (
          <>
            <div style={{ flexGrow: 1, overflowY: 'auto', marginBottom: '1rem' }}>
              <ChatHistory messages={messages} />
              <div ref={messagesEndRef} />
            </div>
            
            <ChatInput 
              onSendMessage={sendMessage} 
              loading={loading} 
              disabled={!currentSession || (!hasPatientContext())}
              placeholder="Ask anything about patient"
            />
          </>
        )}
      </Segment>
    </CardLayout>
  );
};

export default CardAiChat;